import type { BuildingType } from "components/BuildingProfile/types";
import type { MixUseKey } from "features/dt-basic-setup/single-building/model/types";

import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { DEFAULT_MIX_CONFIGURATION, isMixedBuilding } from "features/dt-basic-setup/single-building/model/mix";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const useSummaryStep = () => {
  const { t } = useTranslation();
  const { formData, setFormData } = useContext(SingleBuildingContext);
  const [mixModalOpen, setMixModalOpen] = useState(false);
  const [mixError, setMixError] = useState("");
  const details = formData?.details;

  const [mixDraft, setMixDraft] = useState<Record<MixUseKey, number>>({ ...DEFAULT_MIX_CONFIGURATION });

  const mixConfiguration = useMemo(
    () => details?.mixConfiguration || { ...DEFAULT_MIX_CONFIGURATION },
    [details?.mixConfiguration]
  );

  const mixTotal = useMemo(() => Object.values(mixDraft).reduce((sum, value) => sum + value, 0), [mixDraft]);
  const hasInvalidMixTotal = mixTotal !== 100;

  const updateDetailsField = useCallback(
    (field: "building_type" | "construction_year" | "no_of_floors" | "rental_units", value: number | string) => {
      if (field === "building_type" && isMixedBuilding(value as BuildingType)) {
        setMixDraft({ ...mixConfiguration });
        setMixModalOpen(true);
      }

      const convertedValue = ["construction_year", "no_of_floors", "rental_units"].includes(field)
        ? Number(value)
        : value;

      setFormData({
        ...formData,
        details: { ...(formData?.details ?? {}), [field]: convertedValue },
      });
    },
    [formData, mixConfiguration, setFormData]
  );

  const updateMixValue = useCallback((key: MixUseKey, rawValue: number) => {
    const value = Number.isNaN(rawValue) ? 0 : Math.min(100, rawValue);
    setMixDraft((prev) => ({ ...prev, [key]: value }));
    setMixError("");
  }, []);

  const handleSaveMixConfiguration = useCallback(() => {
    if (mixTotal !== 100) {
      setMixError(t("AddBuilding.summaryStep.mixModal.errorTotal") as string);
      return;
    }

    setFormData({
      ...formData,
      details: {
        ...(formData?.details ?? {}),
        mixConfiguration: mixDraft,
      },
    });
    setMixError("");
    setMixModalOpen(false);
  }, [formData, mixDraft, mixTotal, setFormData, t]);

  const openMixConfigurationModal = useCallback(() => {
    setMixDraft({ ...mixConfiguration });
    setMixModalOpen(true);
  }, [mixConfiguration]);

  return {
    details,
    handleSaveMixConfiguration,
    hasInvalidMixTotal,
    mixConfiguration,
    mixDraft,
    mixError,
    mixModalOpen,
    mixTotal,
    openMixConfigurationModal,
    setMixModalOpen,
    updateDetailsField,
    updateMixValue,
  };
};
